"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { ArrowRight, Play, Star } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ProgramOverview() {
  const {
    ref: eyebrowRef,
    isVisible: eyebrowVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>();
  const {
    ref: subRef,
    isVisible: subVisible
  } = useRevealOnScroll<HTMLParagraphElement>();
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: trustRef,
    isVisible: trustVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const trustItems = [{
    label: "4.9 \u2605 rating"
  }, {
    label: "5,000+ leaders"
  }, {
    label: "Global certification"
  }];
  const avatars = [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696678/img/executive_avatar_1.png",
    fallback: "EM"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696678/img/executive_avatar_2.png",
    fallback: "RK"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696678/img/executive_avatar_3.png",
    fallback: "SJ"
  }];
  return <section data-section-id="877" id="program-overview" className="bg-background text-foreground relative min-h-[90vh] lg:min-h-screen w-full overflow-hidden">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.03] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} />

      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{
      backgroundImage: "radial-gradient(circle, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "24px 24px"
    }} />

      {}
      <div className="pointer-events-none absolute -top-32 -right-32 h-[500px] w-[500px] rounded-lg opacity-60" style={{
      background: "radial-gradient(circle, var(--primary) 0%, transparent 70%)",
      filter: "blur(120px)"
    }} />
      <div className="pointer-events-none absolute -bottom-32 -left-32 h-[500px] w-[500px] rounded-lg opacity-40" style={{
      background: "radial-gradient(circle, var(--secondary) 0%, transparent 70%)",
      filter: "blur(120px)"
    }} />

      {}
      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 pt-24 pb-32 lg:pt-32 lg:pb-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          {}
          <div className="lg:col-span-7 flex flex-col">
            {}
            <div ref={eyebrowRef} data-index={0} className={`flex items-center gap-3 transition-all duration-700 ${eyebrowVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <span className="font-mono text-[12px] uppercase tracking-widest text-primary">COHORT open — ENROLLING NOW</span>
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-lg bg-primary opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-lg bg-primary" />
              </span>
            </div>

            {}
            <h1 ref={headlineRef} className={`font-default mt-6 text-[56px] sm:text-[72px] lg:text-[96px] xl:text-[120px] leading-[0.95] tracking-tight font-semibold transition-all duration-700 delay-100 ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              Master the{" "}
              <span className="italic font-serif text-primary font-normal">
                strategy
              </span>{" "}
              that drives modern growth.
            </h1>

            {}
            <p ref={subRef} className={`font-default mt-8 text-[18px] leading-relaxed text-muted-foreground max-w-[480px] transition-all duration-700 delay-200 ${subVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>Driving growth through strategic and digital marketing. Covers 5 modules: marketing fundamentals, strategic marketing planning, digital marketing, branding &amp; customer experience, and marketing analytics &amp; performance.</p>

            {}
            <div ref={ctaRef} className={`mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 transition-all duration-700 delay-300 ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <Button asChild size="lg" className="group h-12 px-7 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-medium text-base shadow-md">
                
              </Button>

              <Button asChild size="lg" variant="ghost" className="group h-12 px-6 rounded-lg text-foreground hover:bg-accent hover:text-accent-foreground font-medium text-base">
                
              </Button>
            </div>

            {}
            <div ref={trustRef} className={`mt-8 flex flex-wrap items-center gap-x-4 gap-y-2 transition-all duration-700 delay-400 ${trustVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              {trustItems.map((item, i) => <div data-index={i} key={i} className="flex items-center gap-4">
                  <span className="font-mono text-[13px] text-muted-foreground">
                    {item.label}
                  </span>
                  {i < trustItems.length - 1 && <span className="font-mono text-muted-foreground/50">&middot;</span>}
                </div>)}
            </div>
          </div>

          {}
          <div ref={imageRef} className={`lg:col-span-5 relative transition-all duration-1000 delay-200 ${imageVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-6 scale-95"}`}>
            <div className="relative rounded-lg overflow-hidden border border-primary/20 shadow-xl">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696678/img/leadership_mentor_portrait.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696678/img/leadership_mentor_portrait.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696678/img/leadership_mentor_portrait.png-wvc-sizes" alt="Executive Leadership Mentor" loading="lazy" decoding="async" className="w-full h-[480px] sm:h-[560px] lg:h-[640px] object-cover" style={{
              filter: "saturate(0.85) contrast(1.02)"
            }} src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696678/img/executive_avatar_2.png" />

              {}
              <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent pointer-events-none" />
            </div>

            {}
            <div className="absolute bottom-6 left-6 backdrop-blur-md bg-card/80 border border-border rounded-lg px-4 py-3 shadow-lg">
              <div className="flex items-center gap-2.5">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-lg bg-secondary opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-lg bg-secondary" />
                </span>
                <span className="font-mono text-[12px] uppercase tracking-wider text-foreground">Live Session · Strategic AI,MARKETING, AND COMMUNICATION</span>
              </div>
            </div>

            {}
            <div className="absolute top-6 right-6 backdrop-blur-md bg-card/80 border border-border rounded-lg px-4 py-3 shadow-lg">
              <div className="flex items-center gap-3">
                <div className="flex flex-col">
                  <span className="font-default text-2xl font-semibold text-foreground leading-none">
                    4.9
                  </span>
                  <div className="flex items-center gap-0.5 mt-1">
                    {[0, 1, 2, 3, 4].map(i => <Star data-index={i} key={i} className="size-3 fill-primary text-primary" />)}
                  </div>
                </div>
                <div className="flex -space-x-2">
                  {avatars.map((avatar, i) => <SafeHydrate><Avatar data-index={i} key={i} className="size-7 border-2 border-background">
                      <AvatarImage src={avatar.src} alt="Executive Student" />
                      <AvatarFallback className="text-[10px] bg-muted text-muted-foreground font-mono">
                        {avatar.fallback}
                      </AvatarFallback>
                    </Avatar></SafeHydrate>)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-3">
          <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
            DISCOVER
          </span>
          <div className="relative h-12 w-px bg-muted-foreground/30 overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1/2 bg-primary" style={{
            animation: "scrollLine 2s ease-in-out infinite"
          }} />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes scrollLine {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(200%); }
        }
      `}</style>
    </section>;
}